// src/components/admin/performance/TemplateAssignmentModal.js
import React, { useState, useEffect } from 'react';
import { X, Users, Building2, Briefcase, Save, AlertCircle } from 'lucide-react';
import performanceService from '../../../services/performance.service';
import employeeService from '../../../services/employee.service';

const ASSIGNMENT_TYPES = [
    { value: 'employee', label: 'Employees', icon: Users },
    { value: 'department', label: 'Departments', icon: Building2 },
    { value: 'manager', label: 'Managers', icon: Briefcase }
];

const TemplateAssignmentModal = ({ template, onClose }) => {
    const [assignmentType, setAssignmentType] = useState('employee');
    const [selectedItems, setSelectedItems] = useState([]);
    const [availableItems, setAvailableItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAvailableItems();
    }, [assignmentType]);

    const fetchAvailableItems = async () => {
        try {
            setLoading(true);
            // Fetch employees or departments based on assignment type
            if (assignmentType === 'employee' || assignmentType === 'manager') {
                const employees = await employeeService.getAllEmployees();
                setAvailableItems(employees.map(emp => ({
                    id: emp.id,
                    name: `${emp.first_name} ${emp.last_name}`,
                    code: emp.employee_code,
                    designation: emp.designation
                })));
            } else if (assignmentType === 'department') {
                // You'll need to create a department service
                setAvailableItems([]);
            }
        } catch (err) {
            console.error('Error fetching items:', err);
            setError('Failed to load available items');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleItem = (itemId) => {
        setSelectedItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handleAssign = async () => {
        if (selectedItems.length === 0) {
            setError('Please select at least one item');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await performanceService.assignTemplate({
                template_id: template.id,
                assignment_type: assignmentType,
                assignment_ids: selectedItems
            });

            alert('Template assigned successfully!');
            onClose();
        } catch (err) {
            console.error('Error assigning template:', err);
            setError(err.response?.data?.error || 'Failed to assign template');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = availableItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Assign Template</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {template.template_name}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    )}

                    {/* Assignment Type Tabs */}
                    <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
                        {ASSIGNMENT_TYPES.map((type) => {
                            const Icon = type.icon;
                            return (
                                <button
                                    key={type.value}
                                    onClick={() => {
                                        setAssignmentType(type.value);
                                        setSelectedItems([]);
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                                        assignmentType === type.value
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {type.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Search */}
                    <div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={`Search ${assignmentType}...`}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {/* Selection List */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filteredItems.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                No items found
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredItems.map((item) => (
                                    <label
                                        key={item.id}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(item.id)}
                                            onChange={() => handleToggleItem(item.id)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                                            {item.code && (
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {item.code} {item.designation && `• ${item.designation}`}
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Selected Count */}
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedItems.length} {assignmentType}(s) selected
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAssign}
                        disabled={loading || selectedItems.length === 0}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Assign Template
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TemplateAssignmentModal;
